import {
  Configuration,
  PublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";

const host = `${window.location.protocol}//${window.location.host}`;

const getClientId = () => {
  switch (import.meta.env.VITE_STAGE) {
    case "CloudProduction": {
      return "00e37b14-55b5-467a-9333-a1e66182d2ab";
    }
    default: {
      return "dac12150-b0e3-4907-a1f7-27c921c6feff";
    }
  }
};

const getAuthority = () => {
  const directoryId =
    import.meta.env.VITE_STAGE === "CloudProduction"
      ? "f2bbed8e-9ee7-45f8-8981-2e4948c448b4"
      : "1a1663ce-0c39-4e79-84ef-55c15789bf33";

  return `https://login.microsoftonline.com/${directoryId}`;
};

const getScopes = () => {
  const accessId =
    import.meta.env.VITE_STAGE === "CloudProduction"
      ? "82abf776-d1dc-404a-97c4-849ad41b8f18"
      : "33ebf1e7-c3ab-4966-b249-fd3eb7d8ff3f";

  return `api://${accessId}/access_as_user`;
};

const config: Configuration = {
  auth: {
    authority: getAuthority(), // "https://login.microsoftonline.com/1a1663ce-0c39-4e79-84ef-55c15789bf33",
    clientId: getClientId(), //'dac12150-b0e3-4907-a1f7-27c921c6feff',
    // MSAL v5 redirect bridge: auth responses land on the COOP-free redirect.html, which
    // broadcasts them back via BroadcastChannel. Required so silent iframe / ssoSilent flows
    // stop failing with BrowserAuthError: timed_out under Entra's COOP headers.
    // This exact URI (origin + /redirect.html) MUST be registered as a SPA redirect URI in
    // the Azure AD app registration, or login fails with redirect_uri_mismatch (AADSTS50011).
    redirectUri: `${host}/redirect.html`,
    postLogoutRedirectUri: host,
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

export const authProvider = new PublicClientApplication(config);

export const authRequest: RedirectRequest = {
  scopes: [getScopes()],
};

// A promise that never settles: acquireTokenRedirect navigates the browser
// away, so the pending request must not proceed — resolving with undefined
// here used to send requests with "Authorization: Bearer undefined" (and a
// guaranteed 401) in the moment before the redirect happened.
const neverResolves = <T>() => new Promise<T>(() => {});

export const acquireAccessToken = async (
  msalInstance: PublicClientApplication,
): Promise<string> => {
  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();
  const account = activeAccount ?? accounts[0];

  if (!account) {
    await authProvider.acquireTokenRedirect({
      scopes: [getScopes()],
    });
    return neverResolves<string>();
  }

  if (!activeAccount) {
    msalInstance.setActiveAccount(account);
  }

  const request = {
    scopes: [getScopes()],
    account,
  };

  try {
    const authResult = await msalInstance.acquireTokenSilent(request);
    return authResult.accessToken;
  } catch {
    await authProvider.acquireTokenRedirect(request);
    return neverResolves<string>();
  }
};
